export const locales = ["en", "he", "es"] as const;
export type Locale = (typeof locales)[number];
export const defaultLocale: Locale = "en";

export const localeNames: Record<Locale, string> = {
    en: "English",
    he: "עברית",
    es: "Español",
};

// Import translations synchronously using Vite's glob imports
const devMessages = import.meta.glob("../../messages/*.json", { eager: true, import: "default" }) as Record<
    string,
    Record<string, string>
>;
const prodMessages = import.meta.glob("../../messages-prod/*.json", { eager: true, import: "default" }) as Record<
    string,
    Record<string, string>
>;

export function loadMessages(locale: string) {
    if (import.meta.env.PROD) {
        const prodKey = `../../messages-prod/${locale}.json`;
        // empty {} means the prebuild fetch had no API (CI) or failed; the bundled messages must win
        const prod = prodMessages[prodKey];
        if (prod && Object.keys(prod).length > 0) {
            return prod;
        }
        console.warn(`Production translations not found for ${locale}, falling back to dev messages`);
    }
    const devKey = `../../messages/${locale}.json`;
    return devMessages[devKey] || {};
}

/**
 * Translate a key outside of React (e.g. axios interceptors, non-component code).
 * Reads the active locale from the NEXT_LOCALE cookie and interpolates `{param}`
 * placeholders. Falls back to the key itself when no translation exists.
 */
export function translate(key: string, params?: Record<string, string | number>): string {
    const cookieLocale = typeof document !== "undefined" ? getLocaleCookie() : null;
    const locale = cookieLocale && locales.includes(cookieLocale as Locale) ? cookieLocale : defaultLocale;
    const raw = loadMessages(locale)[key] ?? key;
    if (!params) {
        return raw;
    }
    return raw.replace(/\{(\w+)\}/g, (_, name) => String(params[name] ?? `{${name}}`));
}

function getLocaleCookie(): string | null {
    const match = document.cookie.match(/(?:^|;\s*)NEXT_LOCALE=([^;]+)/);
    if (!match) {
        return null;
    }
    try {
        return decodeURIComponent(match[1]);
    } catch {
        return null;
    }
}

// Keep your existing adjustKeys function
export const adjustKeys = (obj: Record<string, any>): Record<string, any> =>
    Object.fromEntries(
        Object.entries(obj).map(([key, value]) =>
            typeof value === "string"
                ? [key.replaceAll(".", "_"), value]
                : [key, typeof value === "object" && value !== null ? adjustKeys(value) : value]
        )
    );
